import { useRef, useEffect, useCallback } from 'react';

// ===================================
// CUSTOM HOOKS
// ===================================

/**
 * Custom hook to handle clicks outside of a referenced element
 * Commonly used for dropdowns, modals, and popover components
 */
export const useClickOutside = <T extends HTMLElement = HTMLElement>(
  onClickOutside: () => void
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClickOutside]);

  return ref;
};

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number; // Distance from bottom to trigger load (in pixels)
  predictiveThreshold?: number; // Distance to trigger predictive prefetch (larger than threshold)
  onPredictiveFetch?: () => void; // Optional predictive data fetch callback
}

export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 200,
  predictiveThreshold = 600, // Fetch data earlier for smoother UX
  onPredictiveFetch
}: UseInfiniteScrollOptions) => {
  const loadingRequestedRef = useRef(false);
  const predictiveFetchRequestedRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef(0);
  const scrollDirectionRef = useRef<'down' | 'up'>('down');
  
  const handleScroll = useCallback(() => {
    // Cancel any pending animation frame to prevent flooding
    if (rafIdRef.current !== null) {
      return;
    }

    // Use requestAnimationFrame to sync with browser paint cycle
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;

      // Calculate scroll position and direction
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

      // Detect scroll direction for predictive fetching
      const scrollDelta = scrollTop - lastScrollTopRef.current;
      if (scrollDelta > 0) {
        scrollDirectionRef.current = 'down';
      } else if (scrollDelta < 0) {
        scrollDirectionRef.current = 'up';
      }
      lastScrollTopRef.current = scrollTop;

      // Predictive data fetching - fetch ahead when scrolling down
      if (
        onPredictiveFetch &&
        !predictiveFetchRequestedRef.current &&
        scrollDirectionRef.current === 'down' &&
        hasMore &&
        !isLoading &&
        distanceFromBottom < predictiveThreshold
      ) {
        predictiveFetchRequestedRef.current = true;
        onPredictiveFetch();
      }

      // Standard infinite scroll - trigger load more
      if (
        !isLoading &&
        hasMore &&
        !loadingRequestedRef.current &&
        distanceFromBottom < threshold
      ) {
        loadingRequestedRef.current = true;
        onLoadMore();
      }
    });
  }, [hasMore, isLoading, onLoadMore, threshold, predictiveThreshold, onPredictiveFetch]);

  // Reset loading request flags when loading state changes
  useEffect(() => {
    if (!isLoading) {
      loadingRequestedRef.current = false;
      predictiveFetchRequestedRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    // Add scroll listener with passive flag for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also check immediately in case content doesn't fill screen
    handleScroll();
    
    return () => {
      // Cleanup scroll listener and cancel any pending RAF
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [handleScroll]);

  return null; // This hook doesn't return anything, just manages side effects
};